import React, { useState } from 'react';
import { 
  Shield, 
  Users, 
  Crown, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Key,
  Mail,
  Phone,
  Building,
  MapPin,
  Copy,
  Eye,
  EyeOff
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { createUserWithRole } from '../../utils/callEdge';

interface AdminAccount {
  id: string;
  email: string;
  password: string;
  profile_type: 'ADMIN' | 'COORDINATEUR' | 'ACCOMPAGNATEUR';
  full_name: string;
  phone: string;
  organization: string;
  address: string;
  city: string;
  postal_code: string;
}

const ADMIN_ACCOUNTS: AdminAccount[] = [
  {
    id: '550e8400-e29b-41d4-a716-446655440001',
    email: 'direction@cress-guadeloupe.org',
    password: 'CressDirection2024!',
    profile_type: 'ADMIN',
    full_name: 'Marie-France CHARLES',
    phone: '0590 83 05 84',
    organization: 'CRESS Guadeloupe',
    address: 'Immeuble Renouveau - ZAC de Houelbourg',
    city: 'Baie-Mahault',
    postal_code: '97122'
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440002',
    email: 'coordination@cress-guadeloupe.org',
    password: 'CressCoord2024!',
    profile_type: 'COORDINATEUR',
    full_name: 'Jean-Claude MARTIN',
    phone: '0590 83 05 85',
    organization: 'CRESS Guadeloupe',
    address: 'Immeuble Renouveau - ZAC de Houelbourg',
    city: 'Baie-Mahault',
    postal_code: '97122'
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440003',
    email: 'accompagnement@cress-guadeloupe.org',
    password: 'CressAccomp2024!',
    profile_type: 'ACCOMPAGNATEUR',
    full_name: 'Sophie BERNARD',
    phone: '0590 83 05 86',
    organization: 'CRESS Guadeloupe',
    address: 'Immeuble Renouveau - ZAC de Houelbourg',
    city: 'Baie-Mahault',
    postal_code: '97122'
  }
];

export const AdminAccountsManager: React.FC = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [creationStatus, setCreationStatus] = useState<Record<string, 'pending' | 'success' | 'error'>>({});
  const [showPasswords, setShowPasswords] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ${message}`]);
  };

  const createAdminAccounts = async () => {
    setIsCreating(true);
    setLogs([]);
    setCreationStatus({});

    addLog('🚀 Début de la création des comptes administrateurs...');
    
    // Vérifier la configuration Supabase
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey || 
        supabaseUrl.includes('placeholder') || 
        supabaseKey.includes('placeholder')) {
      setError('Supabase n\'est pas configuré. Veuillez cliquer sur "Connect to Supabase" en haut à droite.');
      setIsCreating(false);
      return;
    }
    
    addLog(`🔗 URL Supabase: ${supabaseUrl.substring(0, 30)}...`);

    for (const account of ADMIN_ACCOUNTS) {
      try {
        addLog(`📧 Création du compte ${account.email}...`);
        setCreationStatus(prev => ({ ...prev, [account.id]: 'pending' }));

        // Créer l'utilisateur via Edge Function admin
        const result = await createUserWithRole(
          account.email,
          account.password,
          {
            role: account.profile_type.toLowerCase(),
            metadata: {
              full_name: account.full_name,
              phone: account.phone,
              organization: account.organization
            },
            profile_data: {
              profile_type: account.profile_type,
              full_name: account.full_name,
              phone: account.phone,
              organization: account.organization,
              address: account.address,
              city: account.city,
              postal_code: account.postal_code
            }
          },
          supabaseUrl,
          supabaseKey
        );

        addLog(`✅ Compte créé: ${account.email} (${account.profile_type})`);
        setCreationStatus(prev => ({ ...prev, [account.id]: 'success' }));

      } catch (error: any) {
        addLog(`❌ Erreur pour ${account.email}: ${error.message}`);
        console.error('Detailed error for', account.email, error);
        
        if (error.message?.includes('User already registered') || 
            error.message?.includes('already exists')) {
          addLog(`⚠️ Compte existe déjà: ${account.email}`);
          
          // Mettre à jour le profil existant
          try {
            const { error: profileError } = await supabase
              .from('users')
              .upsert({
                profile_type: account.profile_type,
                full_name: account.full_name,
                phone: account.phone,
                organization: account.organization,
                address: account.address,
                city: account.city,
                postal_code: account.postal_code
              }, {
                onConflict: 'email'
              });

            if (profileError) {
              console.warn(`Erreur mise à jour profil pour ${account.email}:`, profileError);
              addLog(`⚠️ Erreur mise à jour profil: ${profileError.message}`);
            } else {
              addLog(`✅ Profil mis à jour: ${account.full_name} (${account.profile_type})`);
              setCreationStatus(prev => ({ ...prev, [account.id]: 'success' }));
            }
          } catch (profileError) {
            addLog(`❌ Erreur profil pour ${account.email}: ${profileError}`);
            setCreationStatus(prev => ({ ...prev, [account.id]: 'error' }));
          }
        } else {
          setCreationStatus(prev => ({ ...prev, [account.id]: 'error' }));
        }
      }
    }

    addLog('🎉 Création des comptes terminée !');
    setIsCreating(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getProfileIcon = (type: string) => {
    switch (type) {
      case 'ADMIN':
        return <Shield className="w-5 h-5 text-red-600" />;
      case 'COORDINATEUR':
        return <Crown className="w-5 h-5 text-purple-600" />;
      case 'ACCOMPAGNATEUR':
        return <Users className="w-5 h-5 text-blue-600" />;
      default:
        return <Users className="w-5 h-5 text-gray-600" />;
    }
  };

  const getProfileColor = (type: string) => {
    switch (type) {
      case 'ADMIN':
        return 'bg-red-50 border-red-200';
      case 'COORDINATEUR':
        return 'bg-purple-50 border-purple-200';
      case 'ACCOMPAGNATEUR':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-pink-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3 mb-2">
          <Shield className="w-8 h-8" />
          <h1 className="text-2xl font-bold">Comptes Administrateurs CRESS</h1>
        </div>
        <p className="text-red-100">
          Création des comptes administrateurs pour l'équipe CRESS Guadeloupe
        </p>
      </div>

      {/* Action Button */}
      <div className="text-center">
        <button
          onClick={createAdminAccounts}
          disabled={isCreating}
          className="px-8 py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl font-semibold text-lg"
        >
          {isCreating ? (
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Création en cours...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <Shield className="w-5 h-5" />
              <span>Créer les comptes administrateurs</span>
            </div>
          )}
        </button>
      </div>

      {/* Accounts Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {ADMIN_ACCOUNTS.map((account) => (
          <div key={account.id} className={`bg-white rounded-xl shadow-sm border p-6 ${getProfileColor(account.profile_type)}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                {getProfileIcon(account.profile_type)}
                <div>
                  <h3 className="font-semibold text-gray-900">{account.profile_type}</h3>
                  <p className="text-sm text-gray-600">{account.full_name}</p>
                </div>
              </div>
              {creationStatus[account.id] && getStatusIcon(creationStatus[account.id])}
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{account.email}</span>
                <button
                  onClick={() => copyToClipboard(account.email)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Copier l'email"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>

              <div className="flex items-center space-x-2">
                <Key className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700 font-mono">
                  {showPasswords ? account.password : '••••••••••••••••'}
                </span>
                <button
                  onClick={() => copyToClipboard(account.password)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Copier le mot de passe"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>

              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{account.phone}</span>
              </div>

              <div className="flex items-center space-x-2">
                <Building className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{account.organization}</span>
              </div>

              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{account.city}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Password Visibility Toggle */}
      <div className="text-center">
        <button
          onClick={() => setShowPasswords(!showPasswords)}
          className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors mx-auto"
        >
          {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          <span>{showPasswords ? 'Masquer' : 'Afficher'} les mots de passe</span>
        </button>
      </div>

      {/* Creation Logs */}
      {logs.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Journal de création</h3>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto">
            <div className="space-y-1 font-mono text-sm">
              {logs.map((log, index) => (
                <div key={index} className={`${
                  log.includes('✅') ? 'text-green-600' :
                  log.includes('❌') ? 'text-red-600' :
                  log.includes('🚀') || log.includes('🎉') ? 'text-blue-600' :
                  'text-gray-600'
                }`}>
                  {log}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Security Instructions */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <h3 className="font-semibold text-amber-900 mb-4">🔐 Instructions de Sécurité</h3>
        
        <div className="space-y-3 text-sm text-amber-800">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Après création des comptes :</p>
              <ul className="mt-1 space-y-1 ml-4">
                <li>• Changez immédiatement tous les mots de passe</li>
                <li>• Activez l'authentification 2FA pour les admins</li>
                <li>• Désactivez l'inscription publique dans Supabase Auth</li>
                <li>• Configurez les domaines autorisés</li>
              </ul>
            </div>
          </div>
          
          <div className="flex items-start space-x-2">
            <Shield className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Configuration Supabase Auth :</p>
              <ul className="mt-1 space-y-1 ml-4">
                <li>• Site URL: https://cressguadeloupe.online</li>
                <li>• Redirect URLs: https://cressguadeloupe.online/auth/callback</li>
                <li>• Disable "Enable email confirmations"</li>
                <li>• Enable "Disable new user signups"</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Manual Creation Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 mb-4">📋 Création Manuelle (Alternative)</h3>
        
        <div className="space-y-4 text-sm text-blue-800">
          <div>
            <p className="font-medium">1. Dans Supabase Dashboard &gt; Authentication &gt; Users :</p>
            <ul className="mt-2 space-y-1 ml-4">
              <li>• Cliquez "Add user"</li>
              <li>• Email: direction@cress-guadeloupe.org</li>
              <li>• Password: CressDirection2024!</li>
              <li>• Auto Confirm User: ✅</li>
            </ul>
          </div>
          
          <div>
            <p className="font-medium">2. Dans SQL Editor, exécutez :</p>
            <div className="mt-2 p-3 bg-white rounded border font-mono text-xs">
              INSERT INTO public.users (id, profile_type, full_name, organization)<br/>
              VALUES ('user-id-from-auth', 'ADMIN', 'Marie-France CHARLES', 'CRESS Guadeloupe');
            </div>
          </div>
          
          <div>
            <p className="font-medium">3. Répétez pour les autres comptes</p>
          </div>
        </div>
      </div>
    </div>
  );
};